import { Col, Row } from "antd";
import { DefaultButton } from "components/Shared/DefaultButton";
import { DefaultModal } from "components/Shared/DefaultModal";
import { FixtureTable } from "components/Shared/FixtureTable";
import { RosterAndStaffTable } from "components/Shared/RosterAndStaffTable";
import { PeopleTypes } from "enums/peopleTypes";
import { useFetchFixtures } from "hooks/useFixturesApi";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useCopyPeople } from "../hooks/useCopyPeople";
import { useCopyPeopleFixturesColumns } from "../hooks/useCopyPeopleFixturesColumns";
import { usePreviewPeople } from "../hooks/usePreviewPeople";

type Props = {
  type: PeopleTypes;
  disabled: boolean;
  entityId: string;
  seasonId: string;
  currentFixtureId: string;
};

export const CopyPeople = ({ type, disabled, entityId, seasonId, currentFixtureId }: Props) => {
  const { formatMessage } = useIntl();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [previewFixtureId, setPreviewFixtureId] = useState<string>();
  const [copyFixtureId, setCopyFixtureId] = useState<string>();
  const [openModal, setOpenModal] = useState(false);

  const { data: fixturesResponse, isLoading } = useFetchFixtures({
    entityId,
    seasonId,
    enabled: openModal,
  });
  const fixtures = (fixturesResponse?.data || []).filter((x: any) => x.fixtureId !== currentFixtureId);

  const {
    records: previewRecords,
    columns: previewColumns,
    isLoading: isPreviewLoading,
  } = usePreviewPeople({
    type,
    entityId,
    seasonId,
    fixtureId: previewFixtureId || "undefined",
    enabled: !!previewFixtureId && openPreviewModal,
  });

  const { mutateAsync: copy, isCopying } = useCopyPeople({
    type,
    entityId,
    seasonId,
    fixtureId: currentFixtureId,
    fromFixtureId: copyFixtureId || "undefined",
  });

  const fixtureColumns = useCopyPeopleFixturesColumns({
    type,
    onCopy: (record: any) => {
      setCopyFixtureId(record.fixtureId);
      setOpenConfirmModal(true);
      setOpenModal(false);
    },
    onPreview: (record: any) => {
      setPreviewFixtureId(record.fixtureId);
      setOpenPreviewModal(true);
      setOpenModal(false);
    },
  });

  const onPreviewModalClosed = () => {
    setOpenPreviewModal(false);
    setPreviewFixtureId(undefined);
    setOpenModal(true);
  };

  const onConfirmModalClosed = () => {
    if (isCopying) {
      return;
    }
    setOpenConfirmModal(false);
    setCopyFixtureId(undefined);
    setOpenModal(true);
  };

  const onCopy = async () => {
    if (!copyFixtureId) {
      return;
    }
    try {
      await copy();
    } finally {
      setOpenConfirmModal(false);
      setCopyFixtureId(undefined);
      setOpenModal(false);
    }
  };

  return (
    <>
      <DefaultButton
        onClick={() => setOpenModal(true)}
        disabled={disabled}
        icon={<i className="fa-solid fa-file-arrow-down" />}
      >
        {formatMessage({
          id:
            type === PeopleTypes.Roster
              ? "button.copyRoster"
              : type === PeopleTypes.Staff
                ? "button.copyStaff"
                : "notSupported",
        })}
      </DefaultButton>
      {openModal && (
        <DefaultModal open={openModal} footer={null} onCancel={() => setOpenModal(false)}>
          <Row className="mt-25">
            <Col span={24}>
              <FixtureTable isLoading={isLoading} fixtures={fixtures} columns={fixtureColumns} />
            </Col>
          </Row>
        </DefaultModal>
      )}
      {openConfirmModal && (
        <DefaultModal
          title={formatMessage({
            id: "copyPeopleModal.confirmationModal.title",
          })}
          open={openConfirmModal}
          onOk={onCopy}
          width={undefined}
          okText={formatMessage({ id: "button.copy" })}
          okButtonProps={{ loading: isCopying }}
          cancelButtonProps={{ disabled: isCopying }}
          onCancel={onConfirmModalClosed}
        >
          {formatMessage(
            {
              id:
                type === PeopleTypes.Roster
                  ? "copyPeopleModal.confirmationModal.content.roster"
                  : type === PeopleTypes.Staff
                    ? "copyPeopleModal.confirmationModal.content.staff"
                    : "notSupported",
            },
            { objectType: formatMessage({ id: "objectType.fixture" }) }
          )}
        </DefaultModal>
      )}
      {openPreviewModal && (
        <DefaultModal open={openPreviewModal} footer={null} onCancel={onPreviewModalClosed}>
          <Row className="mt-25">
            <Col span={24}>
              <RosterAndStaffTable isLoading={isPreviewLoading} records={previewRecords} columns={previewColumns} />
            </Col>
          </Row>
        </DefaultModal>
      )}
    </>
  );
};
